import { useMatomo } from "@jonkoops/matomo-tracker-react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepButton,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FormatListChecks from "mdi-material-ui/FormatListChecks";
import HelpCircleOutline from "mdi-material-ui/HelpCircleOutline";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { getPatientInfoObject } from "../PatientInfo";
import LoadDosingInfoDialog from "./LoadDosingInfoDialog";
import LoadPatientInfoDialog from "./LoadPatientInfoDialog";

function getSteps() {
  return ["Patient Data and Dosing", "Measurements"];
}

const PatientInfoAndDosingDialog = ({
  patientDataDialogOpen,
  handlePatientDataDialogClose,
  patientData,
  handlePatientDataInputChange,
  handlePatientDataSubmit,
  handleHtWtUnitToggle,
  handleAgeInMonthsToggle,
  handleUseMostellerMethodToggle,
  handleCreatUnitChange,
  handleCellValueChange,
  handleAddRow,
  handleAddMeasurementsToPatientData,
  handleRemoveRow,
  handlePatientDataLoad,
  loadGraphForPatientData,
  handleClearData,
  handleDownload,
  loadPatientDialogOpen,
  handleLoadPatientDialogOpen,
  handleLoadPatientDialogClose,
  loadMeasurementsDialog,
  handleLoadMeasurementsDialogOpen,
  handleLoadMeasurementsDialogClose,
  handleTimeInHoursChange,
}) => {
  const { register, handleSubmit, errors, clearErrors, trigger } = useForm({
    mode: "onBlur",
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);
  const [fileLoadErrors, setFileLoadErrors] = useState([]);

  const stepPropToTextField = {
    step: "any",
  };

  const { trackEvent } = useMatomo();

  const handleStep = (step) => async () => {
    if (step === 1) {
      //moving to second tab - validate
      await trigger();

      let valid =
        patientData.identifier &&
        patientData.age &&
        patientData.weight &&
        patientData.height &&
        patientData.dose;

      if (valid) {
        trackEvent({
          category: "Data Entry",
          action: "Move to Measurements Tab",
          name: "Move to Measurements Tab",
        });
        setActiveStep(step);
      }

      return;
    }
    setActiveStep(step);
  };

  const handleUploadedFile = (fileData) => {
    //TODO: populate validation errors in this array
    //error if multiple records are present in a file.
    if (fileData.length > 2) {
      setFileLoadErrors([
        "Multiple patient data records found in the uploaded file. Expected one record.",
      ]);
      return;
    }

    //data struct
    const patientData = getPatientInfoObject();
    //parse fileData and throw Errors if any.

    // identify the total parameters (time, conc, creat) to be added
    if (fileData.length > 1) {
      fileData[0].forEach((header, index) => {
        if (patientData.hasOwnProperty(header)) {
          let value = fileData[1][index];
          if (typeof value === "string") {
            const lower = value.toLowerCase();
            if (lower === "true") {
              value = true;
            } else if (lower === "false") {
              value = false;
            }
          }
          patientData[header] = value;
        } else {
          console.warn("unknown header parsed: ", header, " at ", index);
        }
      });
    }

    handlePatientDataLoad(patientData, "file");

    handleLoadPatientDialogClose();
  };

  const handleSelectionFromLocalStorage = (key) => {
    handleLoadPatientDialogClose();
    let lsPatientData = JSON.parse(
      localStorage.getItem(`mtx-local-data-${key}`),
    );
    handlePatientDataLoad(lsPatientData, "local");
    // clear all the errors
    clearErrors();
  };
  const onSubmit = () => {
    trackEvent({
      category: "Data Entry",
      action: "Save Patient Data",
      name: "Save Patient Data",
    });
    setTimeout(() => {
      // alert(JSON.stringify(patientData, null, 2));
      handlePatientDataSubmit(patientData);
    }, 400);
  };

  const handleImportMeasurements = (
    measurements,
    measTimeInHours,
    defaultCreatUnitInmml,
  ) => {
    handleAddMeasurementsToPatientData(
      measurements,
      measTimeInHours,
      defaultCreatUnitInmml,
    );
    handleLoadMeasurementsDialogClose();
  };

  const steps = getSteps();

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <Grid
            container
            sx={{ alignItems: "flex-start", justifyContent: "center" }}
          >
            <Grid
              item
              sm
              sx={{
                padding: 2.5,
                borderRight: "1px solid #949494",
                [theme.breakpoints.down("sm")]: {
                  borderRight: "none",
                  padding: 1.25,
                  width: 320,
                },
              }}
            >
              <Typography variant="h6">Patient Data</Typography>
              <Grid container sx={{ textAlign: "center" }}>
                <Grid item xs>
                  <Tooltip title="Identifier is required.">
                    <TextField
                      id="identifier"
                      label="Identifier *"
                      variant="standard"
                      name="identifier"
                      value={patientData.identifier}
                      onChange={handlePatientDataInputChange("identifier")}
                      inputRef={register({ required: true })}
                      sx={{ width: 200 }}
                      helperText={
                        Boolean(errors.identifier)
                          ? "Identifier is required"
                          : "Note: Identifier is not saved"
                      }
                      error={Boolean(errors.identifier)}
                      margin="dense"
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs>
                  <Tooltip title="Select Identifier to load">
                    <Fab
                      color="primary"
                      onClick={() => {
                        handleLoadPatientDialogOpen();
                      }}
                    >
                      <FormatListChecks />
                    </Fab>
                  </Tooltip>
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs>
                  <TextField
                    label="Age *"
                    id="age"
                    name="age"
                    sx={{ mt: 0, width: 150 }}
                    variant="standard"
                    inputRef={register({ required: true })}
                    helperText={Boolean(errors.age) ? "Age is required" : ""}
                    error={Boolean(errors.age)}
                    value={patientData.age !== 0 ? patientData.age : ""}
                    onChange={handlePatientDataInputChange("age")}
                    margin="dense"
                    type="number"
                    inputProps={{ step: "1" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {patientData.ageInMonths ? "mos" : "yrs"}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <FormControl component="fieldset" sx={{ pt: "15px" }}>
                    <FormControlLabel
                      value="Age in months"
                      control={
                        <Switch
                          checked={patientData.ageInMonths}
                          color="primary"
                          onChange={handleAgeInMonthsToggle}
                        />
                      }
                      label="Age in months"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl component="fieldset" sx={{ mt: 1.25 }}>
                <FormLabel component="legend">Sex</FormLabel>
                <RadioGroup
                  row
                  aria-label="Sex"
                  name="Sex"
                  value={patientData.sex}
                  onChange={handlePatientDataInputChange("sex")}
                  required={true}
                >
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    label="Female"
                  />
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    label="Male"
                  />
                </RadioGroup>
              </FormControl>

              <Grid container>
                <Grid item container spacing={1}>
                  <Grid item xs={3}>
                    <TextField
                      id="weight"
                      label="Weight"
                      name="weight"
                      variant="standard"
                      value={
                        patientData.weight !== 0.0 ? patientData.weight : ""
                      }
                      onChange={handlePatientDataInputChange("weight")}
                      margin="dense"
                      inputRef={register({ required: true })}
                      helperText={
                        Boolean(errors.weight) ? "weight is required" : ""
                      }
                      error={Boolean(errors.weight)}
                      required={true}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {patientData.metricunits ? "kg" : "lbs"}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      variant="standard"
                      label="Height"
                      id="height"
                      name="height"
                      value={patientData.height !== 0 ? patientData.height : ""}
                      onChange={handlePatientDataInputChange("height")}
                      margin="dense"
                      inputRef={register({ required: true })}
                      helperText={
                        Boolean(errors.height) ? "height is required" : ""
                      }
                      error={Boolean(errors.height)}
                      required={true}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {patientData.metricunits ? "cm" : "in"}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl component="fieldset" sx={{ pt: 1.25 }}>
                      <FormControlLabel
                        value="Imperial Unit"
                        control={
                          <Switch
                            checked={patientData.metricunits === false}
                            color="primary"
                            onChange={handleHtWtUnitToggle}
                          />
                        }
                        label="Imperial Unit"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <br />
              <Grid container direction="column">
                <Grid item container md>
                  <Grid item xs={5}>
                    <Tooltip title="Using Dubois-Dubois method">
                      <Typography variant="body1" sx={{ pt: 0.625 }}>
                        Surface: {patientData.surface} m<sup>2</sup>
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={7}>
                    <FormControl component="fieldset">
                      <FormControlLabel
                        value="Use Mosteller method"
                        control={
                          <Switch
                            checked={patientData.bsaCalcUsingMostellerMethod}
                            color="primary"
                            onChange={handleUseMostellerMethodToggle}
                          />
                        }
                        label="Use Mosteller method"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid item md>
                  <Typography variant="caption">
                    Note: BSA calculation is based on the Dubois-Dubois method
                    by default.
                  </Typography>
                </Grid>
              </Grid>

              <br />
              <Typography variant="body2" paragraph sx={{ mb: "3px" }}>
                Has the patient had a baseline albumin result less than 2.5
                g/dL?
              </Typography>

              <FormControl variant="standard">
                <Select
                  id="albumin-select"
                  value={patientData.baselinealbumin}
                  sx={{ width: 80 }}
                  onChange={handlePatientDataInputChange("baselinealbumin")}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
              <Grid container alignItems="center" sx={{ pt: 1.25 }}>
                <Grid item sx={{ justifyContent: "center" }}>
                  <Typography variant="body2" paragraph sx={{ mb: "3px" }}>
                    Does the patient have Down Syndrome?
                  </Typography>
                </Grid>
                <Grid
                  item
                  sx={{
                    pl: 1.25,
                    [theme.breakpoints.down("md")]: { pl: 0 },
                  }}
                >
                  <FormControl variant="standard">
                    <Select
                      id="down-syndrome-select"
                      value={patientData.down}
                      sx={{ width: 80 }}
                      onChange={handlePatientDataInputChange("down")}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Typography
                variant="body2"
                paragraph
                sx={{ mb: "3px", pt: 1.25 }}
              >
                Did the patient have a pleural effusion at the time of infusion
                or develop one during the elimination of MTX?
              </Typography>

              <FormControl variant="standard">
                <Select
                  id="pleural-effusion-select"
                  value={patientData.peff}
                  sx={{ width: 80 }}
                  onChange={handlePatientDataInputChange("peff")}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              sm
              sx={{
                p: "5px 20px 20px 20px",
                [theme.breakpoints.down("md")]: {
                  p: 1.25,
                  width: 320,
                },
              }}
            >
              <Typography variant="h6" paragraph>
                Dosing
              </Typography>

              <FormControl variant="standard">
                <InputLabel id="patient-indication-select">
                  Indication *
                </InputLabel>
                <Select
                  id="indication"
                  name="indication"
                  value={patientData.indication}
                  sx={{ width: 200 }}
                  onChange={handlePatientDataInputChange("indication")}
                >
                  <MenuItem value={"Leukemia"}>Leukemia</MenuItem>
                  <MenuItem value={"Osteosarcoma"}>Osteosarcoma</MenuItem>
                  <MenuItem value={"Lymphoma"}>Lymphoma</MenuItem>
                </Select>
              </FormControl>

              <br />
              <Tooltip title="Total dose in grams/m&#178;. Can be split into a loading dose (i.e. 10% in first 0.5 h) and main dose.">
                <TextField
                  variant="standard"
                  label="Dose *"
                  id="dose"
                  name="dose"
                  inputRef={register({ required: true })}
                  helperText={Boolean(errors.dose) ? "Dose is required" : ""}
                  error={Boolean(errors.dose)}
                  value={patientData.dose !== 0 ? patientData.dose : ""}
                  onChange={handlePatientDataInputChange("dose")}
                  margin="dense"
                  sx={{ width: 200 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" disableTypography={true}>
                        gm/m&#178;
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
              <br />
              <Box sx={{ mt: 2 }}>
                <Grid container direction="column">
                  <Typography variant="body1">Protocol</Typography>
                  <Grid item container>
                    <Grid item sm>
                      <TextField
                        variant="standard"
                        id="load"
                        label="Load"
                        name="load"
                        inputRef={register({ required: true })}
                        helperText={
                          Boolean(errors.load) ? "Load is required" : ""
                        }
                        error={Boolean(errors.load)}
                        required={true}
                        value={patientData.load}
                        onChange={handlePatientDataInputChange("load")}
                        margin="dense"
                        sx={{ width: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item sm>
                      <TextField
                        variant="standard"
                        label="In"
                        required={true}
                        id="loadin"
                        name="loadin"
                        inputRef={register({ required: true })}
                        helperText={
                          Boolean(errors.loadin) ? "load time is required" : ""
                        }
                        error={Boolean(errors.loadin)}
                        value={patientData.loadin}
                        onChange={handlePatientDataInputChange("loadin")}
                        margin="dense"
                        sx={{ width: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">h</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid item container>
                    <Grid item sm>
                      <TextField
                        variant="standard"
                        label="Main"
                        id="main"
                        name="main"
                        inputRef={register({ required: true })}
                        helperText={
                          Boolean(errors.main) ? "Main is required" : ""
                        }
                        error={Boolean(errors.main)}
                        required={true}
                        value={patientData.main}
                        onChange={handlePatientDataInputChange("main")}
                        margin="dense"
                        sx={{ width: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item sm>
                      <TextField
                        variant="standard"
                        label="In"
                        id="mainin"
                        name="mainin"
                        inputRef={register({ required: true })}
                        helperText={
                          Boolean(errors.mainin) ? "Main time is required" : ""
                        }
                        error={Boolean(errors.mainin)}
                        required={true}
                        value={patientData.mainin}
                        onChange={handlePatientDataInputChange("mainin")}
                        margin="dense"
                        sx={{ width: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">h</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
              <Tooltip title="Number of hours after the start of infusion to simulate">
                <TextField
                  variant="standard"
                  label="Simulation "
                  id="sim"
                  name="sim"
                  value={patientData.sim !== 0 ? patientData.sim : ""}
                  onChange={handlePatientDataInputChange("sim")}
                  margin="dense"
                  sx={{ width: 200 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">h</InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
              <Box sx={{ mt: 2 }}>
                <Grid container direction="column">
                  <Typography variant="body1">Elimination Threshold</Typography>
                  <Grid item container>
                    <Grid item sm>
                      <Tooltip title="Upper limit for elimination threshold">
                        <TextField
                          variant="standard"
                          label="Upper"
                          id="upperconc"
                          name="upperconc"
                          inputRef={register({ required: false })}
                          value={patientData.upperconc}
                          onChange={handlePatientDataInputChange("upperconc")}
                          margin="dense"
                          sx={{ width: 100 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                &#956;M
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item sm>
                      <Tooltip title="Lower limit for elimination threshold">
                        <TextField
                          variant="standard"
                          label="Lower"
                          id="lowerconc"
                          name="lowerconc"
                          inputRef={register({ required: false })}
                          value={patientData.lowerconc}
                          onChange={handlePatientDataInputChange("lowerconc")}
                          margin="dense"
                          sx={{ width: 100 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                &#956;M
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Box
            sx={{
              p: 1.5,
              overflowY: "auto",
            }}
          >
            <table style={{ margin: "0 auto", width: "100%" }}>
              <thead>
                <tr>
                  <th>
                    {patientData.timeInHours ? "Time [h]" : "Date and Time"}
                    {patientData.timeInHours ? (
                      <Tooltip title="Enter time since the start of infusion in hours when MTX concentration and/or creatinine was measured">
                        <IconButton
                          aria-label="Enter time since the start of infusion in hours when MTX concentration and/or creatinine was measured"
                          size="large"
                        >
                          <HelpCircleOutline />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Enter date and time of infusion when MTX concentration and/or creatinine was measured">
                        <IconButton
                          aria-label="Enter date and time of infusion when MTX concentration and/or creatinine was measured"
                          size="large"
                        >
                          <HelpCircleOutline />
                        </IconButton>
                      </Tooltip>
                    )}
                    <FormControlLabel
                      sx={{ display: "block", mt: "-7px" }}
                      control={
                        <Switch
                          checked={patientData.timeInHours === true}
                          color="primary"
                          onChange={handleTimeInHoursChange}
                        />
                      }
                      label={
                        patientData.timeInHours === true
                          ? "Hours"
                          : "Date and Time"
                      }
                    />
                  </th>
                  <th>
                    MTX Conc [&#956;M]
                    <Tooltip title="Enter the MTX concentration in uM at the specified time on the same row">
                      <IconButton
                        aria-label="Enter the MTX concentration in uM at the specified time on the same row"
                        size="large"
                      >
                        <HelpCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </th>
                  <th>
                    {patientData.creatunitmml
                      ? "Creatinine [μmol/L]"
                      : "Creatinine [mg/dL]"}
                    <Tooltip
                      title={
                        patientData.creatunitmml
                          ? "Enter the Creatinine concentration in μmol/L at the specified time on the same row"
                          : "Enter the Creatinine concentration in mg/dL at the specified time on the same row"
                      }
                    >
                      <IconButton
                        aria-label="creatinine concentration"
                        size="large"
                      >
                        <HelpCircleOutline />
                      </IconButton>
                    </Tooltip>
                    <FormControlLabel
                      sx={{ display: "block", mt: "-7px" }}
                      control={
                        <Switch
                          checked={patientData.creatunitmml === true}
                          color="primary"
                          onChange={handleCreatUnitChange}
                        />
                      }
                      label={
                        patientData.creatunitmml === true
                          ? "values in μmol/L Unit"
                          : "values in mg/dL Unit"
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {patientData.parameters.map((row, index) => (
                  <tr key={index}>
                    <td>
                      {patientData.timeInHours ? (
                        <TextField
                          variant="standard"
                          name="time"
                          type="number"
                          size="small"
                          sx={{ width: "100%" }}
                          value={patientData.parameters[index].time}
                          onChange={handleCellValueChange(index)}
                          inputProps={stepPropToTextField}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      ) : (
                        <TextField
                          variant="standard"
                          name="time"
                          type="datetime-local"
                          size="small"
                          sx={{ width: "100%" }}
                          value={patientData.parameters[index].time}
                          onChange={handleCellValueChange(index)}
                          inputProps={stepPropToTextField}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          helperText={
                            index === 0 && "*Dosing induction date and time"
                          }
                          FormHelperTextProps={{
                            sx: { color: "red", fontWeight: "bold" },
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <TextField
                        variant="standard"
                        type="number"
                        step="any"
                        name="conc"
                        size="small"
                        sx={{ width: "80%", pl: 2.5 }}
                        value={patientData.parameters[index].conc}
                        onChange={handleCellValueChange(index)}
                        helperText={
                          patientData.parameters[index].time !== "" &&
                          patientData.parameters[index].conc === ""
                            ? "a numerical value is required"
                            : ""
                        }
                        error={
                          patientData.parameters[index].time !== "" &&
                          patientData.parameters[index].conc === ""
                        }
                        inputProps={stepPropToTextField}
                      />
                    </td>
                    <td>
                      <TextField
                        variant="standard"
                        type="number"
                        name="creat"
                        size="small"
                        sx={{ width: "80%" }}
                        value={patientData.parameters[index].creat}
                        onChange={handleCellValueChange(index)}
                        helperText={
                          patientData.parameters[index].time !== "" &&
                          patientData.parameters[index].creat === "" ? (
                            "a numerical value is required"
                          ) : patientData.parameters[index].creat >
                            1.49 * patientData.parameters[0].creat ? (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <Tooltip
                                title={`Value entered is > 1.5x baseline (${patientData.parameters[0].creat} ${patientData.creatunitmml ? "μmol/L" : "mg/dL"})`}
                                arrow
                              >
                                <span>Significant Increase</span>
                              </Tooltip>
                            </Box>
                          ) : (
                            ""
                          )
                        }
                        error={
                          patientData.parameters[index].time !== "" &&
                          patientData.parameters[index].creat === ""
                        }
                        FormHelperTextProps={{
                          sx: {
                            color:
                              patientData.parameters[index].creat >
                              1.49 * patientData.parameters[0].creat
                                ? "#f78f06ff"
                                : "red",
                            fontWeight: "bold",
                          },
                        }}
                        inputProps={stepPropToTextField}
                      />
                    </td>
                    <td>
                      <IconButton
                        aria-label="delete"
                        onClick={handleRemoveRow(index)}
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} align="end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddRow}
                    >
                      Add Row
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        );
      default:
        return;
    }
  }

  return (
    <Dialog
      open={patientDataDialogOpen}
      onClose={handlePatientDataDialogClose}
      aria-labelledby="patient data and dosing"
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 900,
          [theme.breakpoints.up("md")]: {
            height: 840,
          },
        },
      }}
    >
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Stepper
            nonLinear
            activeStep={activeStep}
            alternativeLabel
            sx={{ p: 0 }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepButton onClick={handleStep(index)}>{label}</StepButton>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ flexGrow: 1 }}>{getStepContent(activeStep)}</Box>
          <Box
            sx={{
              p: "5px 20px",
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              justifyContent: "center",
              alignItems: { lg: "flex-start" },
            }}
          >
            {activeStep === 0 && (
              <>
                <Tooltip
                  title="Download the current patient info and dosing info as a csv file"
                  aria-label="download as csv file"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownload("patientInfo")}
                    sx={{
                      mr: { lg: 2.5 },
                      mb: { xs: 2, lg: 0 },
                    }}
                  >
                    Download patient info
                  </Button>
                </Tooltip>
                <Tooltip title="Save to local" aria-label="Save to local">
                  <Button variant="contained" type="submit" color="primary">
                    Save
                  </Button>
                </Tooltip>
              </>
            )}
            {activeStep === 1 && (
              <>
                <Button
                  sx={{
                    mr: { lg: 2.5 },
                    mb: { xs: 2, lg: 0 },
                    width: { xs: "100%", md: "100%", lg: "auto" },
                  }}
                  onClick={handleLoadMeasurementsDialogOpen}
                  variant="contained"
                  size="large"
                  color="primary"
                >
                  Import measurements
                </Button>
                <Tooltip
                  title="Download the current patient info and dosing info as a csv file"
                  aria-label="download as csv file"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownload("patientInfo")}
                    sx={{
                      mr: { lg: 2.5 },
                      mb: { xs: 2, lg: 0 },
                    }}
                  >
                    Download patient info
                  </Button>
                </Tooltip>
                <Tooltip
                  title="Download the measurements as a csv file"
                  aria-label="download measurements as csv file"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownload("measurements")}
                    sx={{
                      mr: { lg: 2.5 },
                      mb: { xs: 2, lg: 0 },
                    }}
                  >
                    Download Measurements
                  </Button>
                </Tooltip>
                <Tooltip title="Save to local" aria-label="Save to local">
                  <Button variant="contained" type="submit" color="primary">
                    Save
                  </Button>
                </Tooltip>
              </>
            )}
          </Box>
        </form>
        <LoadPatientInfoDialog
          loadPatientDialogOpen={loadPatientDialogOpen}
          fileLoadErrors={fileLoadErrors}
          setFileLoadErrors={setFileLoadErrors}
          handleLoadPatientDialogClose={handleLoadPatientDialogClose}
          handleSelectionFromLocalStorage={handleSelectionFromLocalStorage}
          handleUploadedFile={handleUploadedFile}
        />
        <LoadDosingInfoDialog
          loadMeasurementsDialog={loadMeasurementsDialog}
          fileLoadErrors={fileLoadErrors}
          setFileLoadErrors={setFileLoadErrors}
          handleLoadMeasurementsDialogClose={handleLoadMeasurementsDialogClose}
          handleImportMeasurements={handleImportMeasurements}
        />
      </DialogContent>
      <DialogActions sx={{ display: "block" }}>
        <Button color="primary" onClick={handlePatientDataDialogClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleClearData}>
          Clear
        </Button>
        <Box sx={{ float: "right", pl: 2.5 }}>
          {activeStep === 0 && (
            <Button color="primary" variant="contained" onClick={handleStep(1)}>
              Next
            </Button>
          )}
          {activeStep === 1 && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                trackEvent({
                  category: "User Interaction",
                  action: "Calculate",
                  name: "Calculate",
                });
                loadGraphForPatientData();
              }}
            >
              Calculate
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PatientInfoAndDosingDialog;
